import Kichiri from "./KichiriService";
import UserService from "./UserService";

const EngagementTrackerService = {
	/**
	 * List the current user's tracker forms.
	 * @returns {Promise<Array<{form_name: string, updated_at: string}>>}
	 */
	async listForms() {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.list({}, {}, authToken);
			return (response.data && response.data.forms) || [];
		} catch (error) {
			console.log(error);
		}
		return [];
	},

	/**
	 * Create a new form (optionally with starter columns).
	 * @param {Object} params
	 * @param {string} params.formName
	 * @param {Array<{field_name: string, field_type: string, options?: any, order_index?: number}>} [params.fields]
	 */
	async createForm({ formName, fields, isShared }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.create({ formName, fields: fields || [], isShared }, {}, authToken);
			return response.data && response.data.form;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async renameForm({ formName, newFormName, formOwnerId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.rename({ formName, newFormName, formOwnerId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async deleteForm({ formName, formOwnerId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.delete({ formName, formOwnerId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Share a tracker with the company (isShared="company") or make it private.
	 */
	async setFormShared({ formName, formOwnerId, isShared }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.share({ formName, formOwnerId, isShared }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Persist the tracker's view config (filters, sort, column layout).
	 */
	async saveFormConfig({ formName, formOwnerId, config }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.saveConfig({ formName, formOwnerId, config }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async addField({ formName, field, resource, formOwnerId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.fieldAdd({ formName, field, resource: resource || "engagement", formOwnerId }, {}, authToken);
			return response.data && response.data.field;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async updateField({ fieldId, updates }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.fieldUpdate({ fieldId, updates }, {}, authToken);
			return response.data && response.data.field;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async deleteField({ fieldId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.fieldDelete({ fieldId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async reorderFields({ formName, order, formOwnerId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.fieldReorder({ formName, order, formOwnerId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Get one page of the grid for a form. Rows come back newest-updated first;
	 * `offset` + `limit` pick the page and the response says `has_more`.
	 * `search` keeps only engagements whose name or client name contains the text.
	 * @returns {Promise<{form_name: string, fields: Array, rows: Array, total_count: number, has_more: boolean}|null>}
	 */
	async getGrid({ formName, formOwnerId, includeInactive, filterTree, backofficeMode, offset, limit, search }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.grid(
				{
					formName,
					formOwnerId,
					includeInactive: !!includeInactive,
					filterTree: filterTree || null,
					backofficeMode: backofficeMode || "off",
					offset: offset || 0,
					limit: limit || undefined,
					search: search || undefined,
				},
				{},
				authToken
			);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Upsert one cell value.
	 */
	async upsertValue({ fieldId, engagementId, value }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.valueUpsert({ fieldId, engagementId, value }, {}, authToken);
			return response.data && response.data.value;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Patch a whitelisted native engagement field (name, notes, flow_state_id, due_date, date_in, fiscal_year_end).
	 * @returns {Promise<{engagement_id: number, native: Object} | null>}
	 */
	async patchEngagement({ engagementId, field, value }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.engagementPatch({ engagementId, field, value }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * List a form's columns for a resource (engagement/task/user) — for the column editor.
	 */
	async listFields({ formName, resource, formOwnerId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.fieldList({ formName, resource: resource || "engagement", formOwnerId }, {}, authToken);
			return (response.data && response.data.fields) || [];
		} catch (error) {
			console.log(error);
		}
		return [];
	},

	// ---------- Sub-grids (per-engagement Tasks / Users) ----------

	/**
	 * Load an engagement's task or user sub-grid.
	 * @returns {Promise<Object|null>} { resource, engagement_id, fields, rows, roles, datasource_labels, client_labels? }
	 */
	async getSubGrid({ engagementId, resource, formName, formOwnerId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.subGrid({ engagementId, resource, formName, formOwnerId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	async upsertSubValue({ resource, recordId, fieldId, value, engagementId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.subValueUpsert({ resource, recordId, fieldId, value, engagementId }, {}, authToken);
			return response.data && response.data.value;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Patch a native task field and/or its assignees. Returns the refreshed row.
	 */
	async patchTask({ taskId, field, value, assignedUsers }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.taskPatch({ taskId, field, value, assignedUsers }, {}, authToken);
			return response.data && response.data.row;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Patch a native user field, or (field='engagement_role_id') a member's engagement role.
	 */
	async patchUser({ userId, field, value, engagementId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.tracker.userPatch({ userId, field, value, engagementId }, {}, authToken);
			return response.data && response.data.row;
		} catch (error) {
			console.log(error);
		}
		return null;
	},

	/**
	 * Add a task, or assign/create a user member, in an engagement sub-grid.
	 * @returns {Promise<Object|null>} the new row, or null on error
	 */
	async addSubRow({ engagementId, resource, payload }) {
		let { authToken } = UserService.getUserData();
		let response = await Kichiri.tracker.subRowAdd({ engagementId, resource, payload }, {}, authToken);
		return response.data && response.data.row;
	},
};

export default EngagementTrackerService;
